import { Composable, Inject, Setup, Vue } from '@natureos/vue-facing-decorator';

import { InFlightCell } from '@/common/helpers';
import type { ApiResponse, ApiService } from '@/common/services/api.service-new';
import type {
  ArrPotentialParams,
  ArrPotentialStatsResponse,
  ArrPotentialTileTemplateResponse,
  EudrLulcLossResponse,
  ForestLossResponse,
  HansenTreecover2000Response,
  HansenYearlyLayersResponse,
  MultiPolygonCoords,
  TreecoverStatsResponse,
} from './hansen.types';
import { useHansenStore } from './hansen.store';

@Composable()
export class useHansenService extends Vue {
  @Inject()
  private readonly api!: ApiService;

  @Setup(() => useHansenStore())
  public readonly hansenStore!: ReturnType<typeof useHansenStore>;

  // Per-resource in-flight dedup — concurrent callers join one fetch
  // instead of stampeding. Same pattern as useFireAlertsService /
  // useDeforestationService (keyed) but for our handful of singleton resources.
  private readonly yearlyLayersInFlight = new InFlightCell<ApiResponse<HansenYearlyLayersResponse>>();
  private readonly treecover2000InFlight = new InFlightCell<ApiResponse<HansenTreecover2000Response>>();
  private readonly arrPotentialTemplateInFlight = new InFlightCell<ApiResponse<ArrPotentialTileTemplateResponse>>();

  public async getHansenYearlyLayers(): Promise<ApiResponse<HansenYearlyLayersResponse>> {
    if (this.hansenStore.yearlyLayers) {
      return this.hansenStore.yearlyLayers;
    }
    return this.yearlyLayersInFlight.runOnce(async () => {
      const res = await this.api.get<HansenYearlyLayersResponse>(
        `${import.meta.env.VITE_GEE_API}/hansen/yearly-layers`,
      );
      if (res.isSuccess && res.data) {
        this.hansenStore.setYearlyLayers(res);
      }
      return res;
    });
  }

  public async getTreecover2000(): Promise<ApiResponse<HansenTreecover2000Response>> {
    if (this.hansenStore.treecover2000) {
      return this.hansenStore.treecover2000;
    }
    return this.treecover2000InFlight.runOnce(async () => {
      const res = await this.api.get<HansenTreecover2000Response>(
        `${import.meta.env.VITE_GEE_API}/hansen/treecover-2000`,
      );
      if (res.isSuccess && res.data) {
        this.hansenStore.setTreecover2000(res);
      }
      return res;
    });
  }

  public async getForestLoss(
    coords: MultiPolygonCoords,
    threshold: number,
  ): Promise<ApiResponse<ForestLossResponse>> {
    return this.api.post<ForestLossResponse>(
      `${import.meta.env.VITE_GEE_API}/hansen/forestloss`,
      { data: coords, threshold },
    );
  }

  // Hansen forest loss intersected with its 2020 LULC class — drives the EUDR
  // verdict (loss on natural forest / mangrove breaches compliance). Takes the
  // same `threshold` as getForestLoss: loss is restricted to treecover2000 ≥
  // threshold, so the Σ-all-classes total reconciles with /hansen/forestloss.
  public async getEudrLulcLoss(
    coords: MultiPolygonCoords,
    threshold: number,
  ): Promise<ApiResponse<EudrLulcLossResponse>> {
    return this.api.post<EudrLulcLossResponse>(
      `${import.meta.env.VITE_GEE_API}/eudr/lulc-loss`,
      { data: coords, threshold },
    );
  }

  public async getTreecover2000Stats(
    coords: MultiPolygonCoords,
    threshold: number,
  ): Promise<ApiResponse<TreecoverStatsResponse>> {
    return this.api.post<TreecoverStatsResponse>(
      `${import.meta.env.VITE_GEE_API}/hansen/treecover-2000/stats`,
      { data: coords, threshold },
    );
  }

  // Tile URL is a static template ({z}/{x}/{y} + {year}/{treeCover}/{fao} placeholders);
  // cache it like we do for /hansen/yearly-layers.
  public async getArrPotentialTileTemplate(): Promise<ApiResponse<ArrPotentialTileTemplateResponse>> {
    if (this.hansenStore.arrPotentialTileTemplate) {
      return this.hansenStore.arrPotentialTileTemplate;
    }
    return this.arrPotentialTemplateInFlight.runOnce(async () => {
      const res = await this.api.get<ArrPotentialTileTemplateResponse>(
        `${import.meta.env.VITE_GEE_API}/hansen/arr-potential/tile-template`,
      );
      if (res.isSuccess && res.data) {
        this.hansenStore.setArrPotentialTileTemplate(res);
      }
      return res;
    });
  }

  public async getArrPotentialStats(
    coords: MultiPolygonCoords,
    params: ArrPotentialParams,
  ): Promise<ApiResponse<ArrPotentialStatsResponse>> {
    return this.api.post<ArrPotentialStatsResponse>(
      `${import.meta.env.VITE_GEE_API}/hansen/arr-potential/stats`,
      { data: coords, ...params },
    );
  }
}
